import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../../ThemeContext';
import { APP_URLS } from '../../helpers/url_helper';
import { dataAPI } from '../../helpers/api_service';
import { IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tooltip } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AppIconBlue from '../../assets/AppIconBlue.svg';
import '../../styles/loading-animations.css';

const PAGE_SIZES = [5, 10, 25, 50];

const AssetRecordList = () => {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchAssetRecords = async () => {
      try {
        setIsLoading(true);
        setError('');
        const response = await dataAPI.getAssetRecords({ showLoading: false });
        setRecords(Array.isArray(response?.data) ? response.data : []);
      } catch (apiError) {
        setRecords([]);
        setError(apiError?.message || 'Failed to load asset records.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAssetRecords();
  }, []);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return records;
    return records.filter((item) =>
      [
        item.asset_id,
        item.asset_name,
        item.asset_type,
        item.status,
        item.location,
        item.assigned_to,
      ]
        .join(' ')
        .toLowerCase()
        .includes(query)
    );
  }, [records, search]);

  const startIndex = page * pageSize;
  const pageItems = filtered.slice(startIndex, startIndex + pageSize);

  const handleDeleteAsset = async (asset) => {
    const assetKey = asset?.asset_id || asset?.id || 'this asset';
    const confirmed = window.confirm(
      `Are you sure you want to delete ${assetKey}?`,
    );
    if (!confirmed) return;

    try {
      await dataAPI.deleteAssetRecord(asset.id, { showLoading: false });
      setRecords((prev) => prev.filter((item) => item.id !== asset.id));
    } catch (apiError) {
      alert(apiError?.message || 'Failed to delete asset record.');
    }
  };

  useEffect(() => {
    setPage(0);
  }, [search, pageSize]);

  return (
    <div style={{ padding: '24px', minHeight: '100vh', background: mode === 'dark' ? '#0f0f0f' : '#ffffff', color: mode === 'dark' ? '#fff' : '#111827', fontFamily: 'Urbanist, Arial, sans-serif' }}>
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 220px)' }}>
          <img
            src={AppIconBlue}
            alt="Loading"
            className="pulse-animation-icon"
            style={{ width: 46, height: 46 }}
          />
        </div>
      ) : error ? (
        <div style={{ border: `1px solid ${mode === 'dark' ? '#7f1d1d' : '#fecaca'}`, borderRadius: '12px', padding: '16px', color: mode === 'dark' ? '#fecaca' : '#b91c1c' }}>
          {error}
        </div>
      ) : records.length === 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - 240px)', textAlign: 'center' }}>
          <h2 style={{ margin: 0, marginBottom: '10px', color: mode === 'dark' ? '#ffffff' : '#1f2937' }}>No Asset Records Yet</h2>
          <p style={{ margin: 0, marginBottom: '20px', color: mode === 'dark' ? '#d1d5db' : '#4b5563' }}>
            Start by adding your first asset record.
          </p>
          <button
            type="button"
            onClick={() => navigate(APP_URLS.ASSET_RECORD_NEW)}
            style={{ border: 'none', borderRadius: '8px', padding: '10px 16px', background: '#009391', color: '#fff', cursor: 'pointer', fontWeight: 600 }}
          >
            Add New Asset
          </button>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by Asset ID, Name, Type, Status, Location..."
              style={{
                width: '100%',
                maxWidth: '420px',
                borderRadius: '8px',
                border: '1.5px solid #009391',
                padding: '10px 12px',
                background: mode === 'dark' ? '#111111' : '#ffffff',
                color: mode === 'dark' ? '#ffffff' : '#111111',
                outline: 'none',
                boxShadow: '0 0 0 1px rgba(0, 147, 145, 0.15)',
              }}
            />
            <button
              type="button"
              onClick={() => navigate(APP_URLS.ASSET_RECORD_NEW)}
              style={{ border: 'none', borderRadius: '8px', padding: '10px 16px', background: '#009391', color: '#fff', cursor: 'pointer', fontWeight: 600 }}
            >
              Add New Asset
            </button>
          </div>

          <Paper
            sx={{
              border: `1px solid ${mode === 'dark' ? '#2a2a2a' : '#e5e7eb'}`,
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: 'none',
              backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
            }}
          >
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={thStyle(mode)}>Asset ID</TableCell>
                    <TableCell sx={thStyle(mode)}>Asset</TableCell>
                    <TableCell sx={thStyle(mode)}>Status</TableCell>
                    <TableCell sx={thStyle(mode)}>Location</TableCell>
                    <TableCell sx={thStyle(mode)}>Assigned To</TableCell>
                    <TableCell sx={thStyle(mode)}>Next Service Due</TableCell>
                    <TableCell sx={thStyle(mode)}>Rego / Compliance Due</TableCell>
                    <TableCell sx={{ ...thStyle(mode), textAlign: 'center' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ padding: '24px', textAlign: 'center', color: mode === 'dark' ? '#9ca3af' : '#6b7280' }}>
                        No asset records found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    pageItems.map((item) => (
                      <TableRow key={item.id} sx={{ '& td': { borderTop: `1px solid ${mode === 'dark' ? '#1f2937' : '#f3f4f6'}` } }}>
                        <TableCell sx={tdStyle}>{item.asset_id || '-'}</TableCell>
                        <TableCell sx={tdStyle}>
                          <div style={{ fontWeight: 600 }}>{item.asset_name || '-'}</div>
                          {item.asset_type ? (
                            <div style={{ fontSize: '11px', color: mode === 'dark' ? '#9ca3af' : '#6b7280', marginTop: 2 }}>
                              {item.asset_type}
                            </div>
                          ) : null}
                        </TableCell>
                        <TableCell sx={tdStyle}>
                          <span style={getStatusBadgeStyle(item.status, mode)}>
                            {formatStatusLabel(item.status)}
                          </span>
                        </TableCell>
                        <TableCell sx={tdStyle}>{item.location || '-'}</TableCell>
                        <TableCell sx={tdStyle}>{item.assigned_to_username || item.assigned_to || '-'}</TableCell>
                        <TableCell sx={tdStyle}>{item.next_service_due || '-'}</TableCell>
                        <TableCell sx={tdStyle}>{item.rego_expiry || '-'}</TableCell>
                        <TableCell sx={{ ...tdStyle, textAlign: 'center' }}>
                          <div style={{ display: 'inline-flex', gap: '4px' }}>
                            <Tooltip title="View">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  navigate(`${APP_URLS.ASSET_RECORD}/${item.id}`, {
                                    state: { detailView: 'asset-overview' },
                                  })
                                }
                                sx={{ color: '#009391' }}
                              >
                                <VisibilityOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => navigate(`${APP_URLS.ASSET_RECORD}/${item.id}/edit`)}
                                sx={{ color: '#3b82f6' }}
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Record Service">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  navigate(`${APP_URLS.ASSET_RECORD}/${item.id}`, {
                                    state: { detailView: 'service-records' },
                                  })
                                }
                                sx={{ color: '#f59e0b' }}
                              >
                                <BuildOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteAsset(item)}
                                sx={{ color: '#ef4444' }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(event) => {
                setPageSize(parseInt(event.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={PAGE_SIZES}
              sx={{
                color: mode === 'dark' ? '#d0d5dd' : '#344054',
                borderTop: `1px solid ${mode === 'dark' ? '#1f2937' : '#f3f4f6'}`,
                backgroundColor: mode === 'dark' ? '#0a0a0a' : '#f8f9fa',
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  margin: 0,
                },
              }}
            />
          </Paper>
        </>
      )}
    </div>
  );
};

const thStyle = (mode) => ({
  textAlign: 'left',
  padding: '10px 12px',
  fontSize: '13px',
  fontWeight: 700,
  backgroundColor: mode === 'dark' ? '#111111' : '#f9fafb',
  color: mode === 'dark' ? '#ffffff' : '#111827',
});

const tdStyle = {
  textAlign: 'left',
  padding: '10px 12px',
  fontSize: '13px',
};

const formatStatusLabel = (value) => {
  if (!value) return '-';
  const normalized = String(value).toLowerCase();
  switch (normalized) {
    case 'active':
      return 'Active';
    case 'in_use':
    case 'in use':
      return 'In Use';
    case 'out_of_service':
    case 'out of service':
      return 'Out of Service';
    default:
      return String(value).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
};

const getStatusBadgeStyle = (value, mode) => {
  const base = {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '999px',
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'capitalize',
    border: '1px solid transparent',
  };
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'active') {
    return {
      ...base,
      background: mode === 'dark' ? 'rgba(16,185,129,0.15)' : '#ecfdf5',
      color: '#059669',
      borderColor: mode === 'dark' ? 'rgba(16,185,129,0.4)' : '#a7f3d0',
    };
  }
  if (normalized === 'in_use' || normalized === 'in use') {
    return {
      ...base,
      background: mode === 'dark' ? 'rgba(59,130,246,0.15)' : '#eff6ff',
      color: '#2563eb',
      borderColor: mode === 'dark' ? 'rgba(59,130,246,0.4)' : '#bfdbfe',
    };
  }
  if (normalized === 'out_of_service' || normalized === 'out of service') {
    return {
      ...base,
      background: mode === 'dark' ? 'rgba(239,68,68,0.15)' : '#fef2f2',
      color: '#dc2626',
      borderColor: mode === 'dark' ? 'rgba(239,68,68,0.4)' : '#fecaca',
    };
  }
  return {
    ...base,
    background: mode === 'dark' ? '#1f2937' : '#f3f4f6',
    color: mode === 'dark' ? '#e5e7eb' : '#374151',
    borderColor: mode === 'dark' ? '#374151' : '#e5e7eb',
  };
};

export default AssetRecordList;
